/***
 *
 *   TICKET CARD
 *   Kanban card. Ported from ui-reference/src/components/tickets/ticket-card.tsx.
 *
 **********/

import { Icon, Badge, cn } from 'components/lib';
import Style from './ticket.tailwind';
import { useSearchParams } from 'react-router-dom';
import { useTranslate } from 'app/translations';
import { PriorityBadge } from './priorityBadge';
import { AssigneeAvatars } from './assigneeAvatars';
import { isTicketOverdue } from 'utilities/ticketDue';
import { findCurrStatus } from 'utilities/tickets';
import * as dayjs from 'dayjs';

export const priorityColor = {
    urgent: `bg-error/10 text-error`,
    high: `bg-warning/10 text-warning`,
    medium: `bg-primary-50 text-primary-600`,
    low: `bg-success/10 text-success`,
};

export const TicketItem = (props) => {
    const { _t } = useTranslate();
    let [searchParams, setSearchParams] = useSearchParams();
    const { data, onItemClick, className, dragging } = props;
    const {
        subject,
        text,
        priority,
        due_date,
        is_synced,
        is_archived,
        number,
        ticket_assignee,
        histories,
    } = data;

    const statusKey = findCurrStatus(data);
    const overdue = isTicketOverdue({ due_date, status: statusKey });

    const numOfComments = (histories || []).filter((history) => {
        return history.action_type === 'comment_posted';
    });

    return (
        <button
            type="button"
            className={cn(Style.card, dragging && 'rotate-2 shadow-lg', className)}
            onClick={() => {
                searchParams.set('id', number);
                setSearchParams(searchParams);
                onItemClick && onItemClick();
            }}>
            <div className="mb-3 flex items-start gap-2">
                <p className={cn(Style.subject, 'mb-0 flex-1')}>{subject || text}</p>
                {(!is_synced || is_archived) && (
                    <Badge
                        icon="alert-circle"
                        sizeIcon={14}
                        className="shrink-0 bg-warning/10 p-0 text-warning"
                    />
                )}
            </div>

            {due_date && (
                <div
                    className={cn(Style.due, overdue ? Style.dueOverdue : Style.dueIdle)}
                    title={dayjs(due_date).format('MMM D, YYYY')}>
                    <Icon image="calendar" size={14} color="currentColor" className="shrink-0" />
                    <span className={cn('min-w-0', overdue ? 'text-error' : 'font-medium text-text-secondary')}>
                        {overdue ? (
                            <>
                                <span>{_t('ticket.kanban.overdue')}</span>
                                <span className="font-normal text-error/90">
                                    {' · '}
                                    {dayjs(due_date).format('MMM D')}
                                </span>
                            </>
                        ) : (
                            <>
                                {_t('ticket.kanban.dueOn')}
                                <span className="text-text-secondary">
                                    {' · '}
                                    {dayjs(due_date).format('MMM D')}
                                </span>
                            </>
                        )}
                    </span>
                </div>
            )}

            <div className={Style.footer}>
                <PriorityBadge priority={priority} />

                <div className={Style.meta}>
                    <AssigneeAvatars assignees={ticket_assignee || []} max={2} />
                    {numOfComments.length > 0 && (
                        <span className={Style.comments}>
                            <Icon image="message-square" size={12} color="currentColor" />
                            {numOfComments.length}
                        </span>
                    )}
                </div>
            </div>
        </button>
    );
};
